import { useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';

import ChevronRightSmallSolid from '@simplywallst/ui-core/icons/ChevronRightSmallSolid';

import type { PortfolioDetailedReturns } from '../../hooks/usePortfolioDetailedReturns';

import {
  getTableOverviewColumns,
  getTableOverviewColumnsWithNoDataTooltips,
} from './utils';

import { Total } from './Total';
import { TotalPercent } from './TotalPercent';
import { usePortfolioType } from '../../../HoldingsTable/hooks';
type Props = {
  portfolioDetailedReturns: PortfolioDetailedReturns;
};

export const TableOverview = ({ portfolioDetailedReturns }: Props) => {
  const { currencyISO, holdings, liquidatedHoldings, detailedReturns } =
    portfolioDetailedReturns;

  const portfolioType = usePortfolioType(portfolioDetailedReturns);
  const notTransactionalPortfolio = portfolioType !== 'transaction-portfolio';

  const columns = useMemo(
    () =>
      notTransactionalPortfolio
        ? getTableOverviewColumnsWithNoDataTooltips(currencyISO)
        : getTableOverviewColumns(currencyISO),
    [currencyISO, notTransactionalPortfolio]
  );

  const {
    costBasis,
    currencyGains,
    value,
    dividends,
    realizedGains,
    unrealizedGains,
    totalGains,
    annualisedReturn,
    unrealizedGainsPercent,
    realizedGainsPercent,
    dividendsPercent,
    currencyGainsPercent,
    totalGainsPercent,
  } = detailedReturns;

  const holdingsTable = useReactTable({
    getCoreRowModel: getCoreRowModel(),
    columns,
    data: holdings,
  });

  const liquidatedHoldingsTable = useReactTable({
    getCoreRowModel: getCoreRowModel(),
    columns,
    data: liquidatedHoldings,
  });

  const rows = holdingsTable.getRowModel().rows;
  const liquidatedRows = liquidatedHoldingsTable.getRowModel().rows;

  return (
    <>
      <thead>
        {holdingsTable.getHeaderGroups().map((headerGroup) => (
          <tr
            key={`table-overview-${headerGroup.id}`}
            className="top-[63px] z-1 h-[58px] xl:sticky"
            aria-label="Overview Header"
          >
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                className="border-b border-l-0 border-r border-t-0 border-solid border-soft bg-surface-2 p-x2 py-x1_5 text-right align-bottom text-tiny font-strong leading-tight text-soft first:text-left"
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                )}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr
            key={`${row.original.id}-holding`}
            className="group"
            aria-label={`${row.original.company.uniqueSymbol} Holding Overview`}
          >
            {row.getVisibleCells().map((cell, index) => {
              return (
                <td
                  key={cell.id}
                  className="relative border-b border-l-0 border-r border-t-0 border-solid border-soft bg-surface-1 p-x2 py-x1_25 text-right text-xs first:text-left group-hover:bg-surface-2 [&:nth-child(10)]:bg-brand-01-softer [&:nth-child(10)]:group-hover:bg-brand-01-soft [&:nth-child(n+6):nth-child(-n+9)]:bg-brand-01-subtle [&:nth-child(n+6):nth-child(-n+9)]:group-hover:bg-brand-01-softer"
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  {index === 0 && (
                    <a
                      href={`#${row.original.company.uniqueSymbol}`}
                      className="absolute inset-0 inset-y-x0 grid items-center justify-end opacity-0 transition-opacity group-hover:opacity-100"
                    >
                      <ChevronRightSmallSolid className="fill-solid" />
                    </a>
                  )}
                </td>
              );
            })}
          </tr>
        ))}

        {liquidatedRows.length > 0 && (
          <>
            <tr className="bg-surface" aria-label="Liquidated Holdings">
              <td
                colSpan={columns.length}
                className="border-x-0 border-b border-t-0 border-solid border-soft px-x2 py-x0_75 text-left text-tiny font-strong text-soft"
              >
                <span className="uppercase">Liquidated Holdings</span>
              </td>
            </tr>
            {liquidatedRows.map((row) => (
              <tr
                key={`${row.original.id}-liquidated-holding`}
                className="group"
                aria-label={`${row.original.company.uniqueSymbol} Holding Overview (liquidated)`}
              >
                {row.getVisibleCells().map((cell, index) => {
                  return (
                    <td
                      key={cell.id}
                      className="relative border-b border-l-0 border-r border-t-0 border-solid border-soft bg-surface-1 p-x2 py-x1_25 text-right text-xs first:text-left group-hover:bg-surface-2 [&:nth-child(10)]:bg-brand-01-softer [&:nth-child(10)]:group-hover:bg-brand-01-soft [&:nth-child(n+6):nth-child(-n+9)]:bg-brand-01-subtle [&:nth-child(n+6):nth-child(-n+9)]:group-hover:bg-brand-01-softer"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                      {index === 0 && (
                        <a
                          href={`#${row.original.company.uniqueSymbol}`}
                          className="absolute inset-0 inset-y-x0 grid items-center justify-end opacity-0 transition-opacity group-hover:opacity-100"
                        >
                          <ChevronRightSmallSolid className="fill-solid" />
                        </a>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </>
        )}

        <Total
          annualisedReturn={annualisedReturn}
          costBasis={costBasis}
          currencyGains={currencyGains}
          value={value}
          dividends={dividends}
          realizedGains={realizedGains}
          unrealizedGains={unrealizedGains}
          totalGains={totalGains}
          notTransactionalPortfolio={notTransactionalPortfolio}
        />

        <TotalPercent
          unrealizedGainsPercent={unrealizedGainsPercent}
          realizedGainsPercent={realizedGainsPercent}
          dividendsPercent={dividendsPercent}
          currencyGainsPercent={currencyGainsPercent}
          totalGainsPercent={totalGainsPercent}
          notTransactionalPortfolio={notTransactionalPortfolio}
        />
      </tbody>
    </>
  );
};
